/* eslint-disable no-console */
import { useState, useEffect, useMemo } from "react";
import {
  client,
  getClientes,
  getContratos,
  getDepreciacionYAmortizacion,
  getEdificios,
  getIntermediarios,
  getOportunidades,
  getUsers,
  getEmpleados,
  getRemuneracionesEmpleado,
  getInversionMensual,
  getValorActivosMensual,
  getCostosFijos,
  getCostosHardwareFlex,
  getCostosPublicidad,
} from "../../../utils";
import { cargarSnapshotComision, cargarSnapshotPctCongelado } from "../../../utils/comisionSnapshot";
import { cargarCostosIntermediacion } from "../../../utils/costosIntermediacion";
import { esNotaCredito, TIPO_DTE_DIN } from "../../../utils/dte";
import { loadExchangeRates } from "../../../utils/exchangeRateService";
import { parseLocalYMD } from "../../../utils/fechasFuturosPanel";
import { refKeyDeVinculacion } from "../../../utils/lineasFacturacion/refKey";
import { construirExclusionNcRecibidas, INICIO_COSTOS_SII } from "../costosER";
import { construirExclusionNcEmitidas } from "../ingresosER";

// Fix H7 (bug de huso horario): new Date("YYYY-MM-DD") parsea como UTC medianoche,
// y en husos con offset negativo (Chile/Perú) el día 1 de cada mes cae en el mes anterior.
const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);

const PAISES = ["Chile", "Perú"];
// Mismos campos que ER/constants.js:21 (se duplica la lista para no acoplar el hook
// de datos a las constantes de presentacion del v1).
const CAMPOS_COSTOS_FIJOS = [
  "oficina",
  "transporte",
  "telefonia",
  "marketing",
  "contador",
  "abogados",
  "gastos_representacion",
  "asesoria_externa",
  "alojamiento",
  "viajes_gerencia",
  "gastos_bancarios",
  "varios",
];
const CERO12 = () => Array(12).fill(0);
// Referencia estable: devolver `[]` nuevo en cada render invalidaría los memos del motor.
const VACIO = [];

const porId = (rows) =>
  (rows || []).reduce((acc, r) => {
    if (r.id) acc[r.id] = r;
    return acc;
  }, {});

const clientesPorId = (rows) =>
  (rows || []).reduce((acc, c) => {
    // El v1 normaliza `agencia` vacía a null; se replica igual.
    if (c.id) acc[c.id] = { ...c, agencia: c.agencia || null };
    return acc;
  }, {});

/**
 * Carga de datos del ER v2 — AMBOS PAÍSES, igual que el v1.
 *
 * Replica los efectos de carga del ER v1 (`ER/index.js`). Devuelve los datos con el
 * mismo desdoblamiento `*Chile` / `*Peru` que consumen los hooks de cálculo cuando
 * `modoCombinado` está activo, más los alias del país de sesión.
 *
 * `costos_gestion_contenidos` se carga aunque sea tabla de costos manuales: aporta la
 * `fecha_factura` con la que los bloques B y C reconocen el INGRESO del mes.
 *
 * @param {string} pais - País de la sesión
 * @param {number} selectedYear - Año seleccionado
 */
export const useERv2Data = (pais, selectedYear) => {
  const [porPais, setPorPais] = useState({});
  const [users, setUsers] = useState({});
  // Catálogo de proveedores { [id]: { nombre, alias } } para MOSTRAR el alias en el detalle de
  // costos (regla 10.1). Global: sin filtrar por país ni habilitado (los ids son globales y un
  // documento viejo puede apuntar a un proveedor deshabilitado o del otro país).
  const [proveedoresPorId, setProveedoresPorId] = useState({});
  const [costosGestionData, setCostosGestionData] = useState([]);
  const [depAmort, setDepAmort] = useState({});
  // FASE E2c: gemelo de `depAmort`, un año atrás — lo necesita la ventana LTM (antes su
  // "año anterior" era `ZEROS` a secas, la porción de la ventana que cae en el año previo
  // mostraba Depreciación/Amortización en cero). Mismo fetch (`getDepreciacionYAmortizacion`),
  // evaluado en `selectedYear - 1`.
  const [depAmortAnoAnterior, setDepAmortAnoAnterior] = useState({});
  // Tablas de costo MANUAL del v1. Alimentan los meses anteriores a 2026-01-01, donde
  // el motor de facturas no aplica (ver costosER.js).
  const [manual, setManual] = useState({});
  const [inversionPorPais, setInversionPorPais] = useState({});
  const [valorActivosPorPais, setValorActivosPorPais] = useState({});
  // Las tres fuentes de costo por documento se guardan POR PAÍS, no solo las de la sesión.
  // El modo combinado necesita la pata de Chile aunque la sesión sea Perú: `pais` sale del
  // HOSTNAME (visiona.pe → Perú, wecast.cl → Chile; ver context/auth.js), así que filtrar la
  // query por él hacía desaparecer las facturas de Chile del agregado de región.
  const [facturasSIIPorPais, setFacturasSIIPorPais] = useState({});
  const [facturasExternasPorPais, setFacturasExternasPorPais] = useState({});
  const [boletasHonorariosPorPais, setBoletasHonorariosPorPais] = useState({});
  // La porción de la sesión, para la vista de país único. Se deriva de los mapas por país en
  // vez de guardarse aparte: un solo origen de verdad, sin riesgo de que queden desfasados.
  const facturasSII = useMemo(() => facturasSIIPorPais[pais] || VACIO, [facturasSIIPorPais, pais]);
  const facturasExternas = useMemo(() => facturasExternasPorPais[pais] || VACIO, [facturasExternasPorPais, pais]);
  const boletasHonorarios = useMemo(() => boletasHonorariosPorPais[pais] || VACIO, [boletasHonorariosPorPais, pais]);

  const [empleadosPorPais, setEmpleadosPorPais] = useState({});
  const [dtesSinVincular, setDtesSinVincular] = useState(0);
  const [siiVinculacionesMap, setSiiVinculacionesMap] = useState(null);
  // PASO 5 (3.9) — facturas SII EMITIDAS de Chile (cualquier año, el filtro por `ano` lo hacen
  // `registrosIngresoSII`/`construirRefKeysVinculadosSII` por dentro, mismo patrón que
  // `oportunidades`/`negociosCarteleria`). SOLO Chile — Perú queda congelado (10.267), nunca se
  // consulta esta tabla para Perú.
  const [facturasEmitidasChile, setFacturasEmitidasChile] = useState([]);
  // PASO 5c (2026-10-07) — `linea_id → lineas_facturacion.tipo`, para resolver el Bloque B/C real
  // de cada vinculación de `negocios_carteleria` (su `venta_tipo` NUNCA distingue hardware OPEX
  // de gestión — ambos comparten el mismo string, solo su LÍNEA los distingue). Tabla global, sin
  // `pais` propio (se filtra por los `linea_id` que de verdad aparecen en `facturasEmitidasChile`,
  // ya Chile-only) — se trae completa, es chica (cientos de filas).
  const [lineaIdATipo, setLineaIdATipo] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);

  // ─── Ventas, contratos y catálogos de AMBOS países (no dependen del año) ───
  useEffect(() => {
    let cancelado = false;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await loadExchangeRates();

        const porPaisData = {};
        await Promise.all(
          PAISES.map(async (p) => {
            const [opps, ctrs, cli, inter, edif, snap, costos, pct, negocios, btl] = await Promise.all([
              getOportunidades({ pais: p }).catch(() => []),
              getContratos({ pais: p }).catch(() => []),
              getClientes({ pais: p }).catch(() => []),
              getIntermediarios({ pais: p }).catch(() => []),
              getEdificios({ pais: p }).catch(() => []),
              cargarSnapshotComision(p).catch(() => null),
              cargarCostosIntermediacion(p).catch(() => null),
              cargarSnapshotPctCongelado(p).catch(() => null),
              client.from("negocios_carteleria").select("*").eq("pais", p).neq("eliminada", true),
              client.from("sales_btl").select("*").eq("pais", p).eq("eliminada", false),
            ]);

            porPaisData[p] = {
              oportunidades: (opps || []).filter((o) => o.pais === p),
              contratos: ctrs || [],
              clientes: clientesPorId(cli),
              intermediarios: porId(inter),
              edificios: porId(edif),
              snapMap: snap,
              costosMap: costos,
              pctMap: pct,
              negociosCarteleria: negocios.data || [],
              salesBTL: btl.data || [],
            };
          })
        );

        const [usuarios, provRows] = await Promise.all([
          getUsers().catch(() => []),
          client
            .from("proveedores")
            .select("id, nombre, alias")
            .then((r) => r.data || [])
            .catch(() => []),
        ]);
        const provMap = (provRows || []).reduce((acc, p) => {
          if (p.id != null) acc[p.id] = { nombre: p.nombre, alias: p.alias };
          return acc;
        }, {});

        if (cancelado) return;
        setPorPais(porPaisData);
        setUsers(porId(usuarios));
        setProveedoresPorId(provMap);
      } catch (err) {
        console.error("ERv2: error cargando datos base", err);
      } finally {
        if (!cancelado) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelado = true;
    };
  }, []);

  // PASO 5 (3.9) — facturas SII EMITIDAS de Chile + su vinculación, SIEMPRE Chile (igual que
  // `oportunidadesChile`/`negociosCarteleriaChile`, independiente de la sesión activa — lo
  // necesita `chileLeavesUSDFijo` desde cualquier sesión, PASO 4b). Sin filtro de año: 183 filas
  // en todo el histórico (medido, PARTE 1) — el filtro por `ano` lo hacen `registrosIngresoSII`/
  // `construirRefKeysVinculadosSII` por dentro. El embed `facturas_sii_vinculaciones(...)` es el
  // MISMO patrón ya resuelto por el mock de los tests de línea base dorada
  // (`parsearEmbeds`/`EMBED_FK`, `lineaBaseDorada.test.js`) — solo se amplían las columnas
  // pedidas (antes `venta_tipo/venta_ref_id/venta_ref_index/linea_id` para el aviso; acá se
  // agrega `monto_aplicado`, necesario para el ingreso). `linea_id` ya estaba pedido (lo usaba el
  // aviso) — PASO 5c lo reusa para resolver Bloque B/C de `negocios_carteleria` (ver `lineaIdATipo`).
  useEffect(() => {
    let cancelado = false;
    const fetchFacturasEmitidasChile = async () => {
      try {
        const { data } = await client
          .from("facturas_sii_emitidas")
          .select(
            "id, folio, fecha_emision, tipo_dte, monto_neto, monto_exento, estado_vinculacion, " +
              "tipo_relacion_nc, documento_referenciado_id, " +
              "facturas_sii_vinculaciones(venta_tipo, venta_ref_id, venta_ref_index, monto_aplicado, linea_id)"
          )
          .eq("pais", "Chile");
        if (!cancelado) setFacturasEmitidasChile(data || []);
      } catch {
        if (!cancelado) setFacturasEmitidasChile([]);
      }
    };
    fetchFacturasEmitidasChile();
    return () => {
      cancelado = true;
    };
  }, []);

  // PASO 5c (2026-10-07) — `lineas_facturacion.tipo` por id, para `resolverBloqueLineaCarteleria`
  // (`src/utils/lineasFacturacion/resolverLinea.js`). Tabla chica y sin `pais` propio — se trae
  // completa (no hace falta scoping, los `linea_id` de Perú simplemente no aparecen en ninguna
  // vinculación de `facturasEmitidasChile`).
  useEffect(() => {
    let cancelado = false;
    const fetchLineasFacturacion = async () => {
      try {
        const { data } = await client.from("lineas_facturacion").select("id, tipo");
        if (cancelado) return;
        setLineaIdATipo(new Map((data || []).map((l) => [Number(l.id), l.tipo])));
      } catch {
        if (!cancelado) setLineaIdATipo(new Map());
      }
    };
    fetchLineasFacturacion();
    return () => {
      cancelado = true;
    };
  }, []);

  // ─── Datos dependientes del año ──────────────────────────────
  useEffect(() => {
    if (!pais || !selectedYear) return;
    let cancelado = false;

    const fetchAno = async () => {
      try {
        const [
          gestion,
          depChile,
          depPeru,
          depChileAA,
          depPeruAA,
          sii,
          externas,
          honorarios,
          invCL,
          invPE,
          valCL,
          valPE,
        ] = await Promise.all([
          client.from("costos_gestion_contenidos").select("*").eq("pais", pais).eq("ano", selectedYear),
          getDepreciacionYAmortizacion("Chile", selectedYear),
          getDepreciacionYAmortizacion("Perú", selectedYear),
          // FASE E2c: mismo fetch, año anterior — lo necesita la ventana LTM.
          getDepreciacionYAmortizacion("Chile", selectedYear - 1),
          getDepreciacionYAmortizacion("Perú", selectedYear - 1),
          // Fase 2: fuentes del costo automático. Se traen sin filtrar por año; el motor
          // filtra por fecha_emision para no depender de una columna de período.
          // Corte duro: el costo del SII arranca el 2026-01-01 (ver costosER.js).
          // Tampoco se TRAEN facturas anteriores, no solo se descartan al computar.
          // SIN filtro de país: se traen los dos y se parten acá. El combinado arma una pata
          // por país y la de Chile no puede depender de desde qué dominio se entró.
          client.from("facturas_sii_recibidas").select("*").gte("fecha_emision", INICIO_COSTOS_SII),
          client.from("facturas_externas").select("*").gte("fecha_emision", INICIO_COSTOS_SII),
          // Tercera fuente de costo: Boletas de Honorarios por documento. El filtro por
          // `computa` (vigentes) lo hace el motor, no la query — misma razón que el resto de
          // las reglas de negocio: viven en costosER.js, donde se testean.
          client.from("boletas_honorarios").select("*").gte("fecha_emision", INICIO_COSTOS_SII),
          // Checkbox "Activos": inversion y valor en libros por pais.
          getInversionMensual("Chile", selectedYear).catch(() => CERO12()),
          getInversionMensual("Perú", selectedYear).catch(() => CERO12()),
          getValorActivosMensual("Chile", selectedYear).catch(() => CERO12()),
          getValorActivosMensual("Perú", selectedYear).catch(() => CERO12()),
        ]);

        if (cancelado) return;
        setCostosGestionData(gestion.data || []);
        setDepAmort({ Chile: depChile, Perú: depPeru });
        setDepAmortAnoAnterior({ Chile: depChileAA, Perú: depPeruAA });
        const partirPorPais = (filas) => ({
          Chile: (filas || []).filter((f) => f.pais === "Chile"),
          Perú: (filas || []).filter((f) => f.pais === "Perú"),
        });
        setFacturasSIIPorPais(partirPorPais(sii.data));
        setFacturasExternasPorPais(partirPorPais(externas.data));
        setBoletasHonorariosPorPais(partirPorPais(honorarios.data));
        setInversionPorPais({ Chile: invCL || CERO12(), Perú: invPE || CERO12() });
        setValorActivosPorPais({ Chile: valCL || CERO12(), Perú: valPE || CERO12() });
      } catch (err) {
        console.error("ERv2: error cargando datos del año", err);
      }
    };

    fetchAno();
    return () => {
      cancelado = true;
    };
  }, [pais, selectedYear]);

  // ─── Tablas de costo manual (pre-2026) ──────────────────────
  useEffect(() => {
    if (!pais || !selectedYear) return;
    let cancelado = false;

    // Misma normalizacion que el v1 (ER/index.js:1026): un objeto por mes con todos
    // los campos, mas asesoria_externa_usd.
    const normFijos = (filas) =>
      Array.from({ length: 12 }, (_, i) => {
        const row = (filas || []).find((r) => Number(r.mes) === i + 1);
        const mesData = {};
        CAMPOS_COSTOS_FIJOS.forEach((campo) => {
          mesData[campo] = Number(row?.[campo]) || 0;
        });
        mesData.asesoria_externa_usd = Number(row?.asesoria_externa_usd) || 0;
        mesData.asesoria_externa_fecha = row?.asesoria_externa_fecha || null;
        return mesData;
      });

    // Publicidad: null = sin dato (el v1 proyecta), numero = valor real (incluso 0).
    const normPublicidad = (filas) =>
      Array.from({ length: 12 }, (_, i) => {
        const row = filas?.[i];
        if (!row) return null;
        return {
          servicios_internet: row.servicios_internet == null ? null : Number(row.servicios_internet),
          software_ds: row.software_ds == null ? null : Number(row.software_ds),
          mantenimiento: row.mantenimiento == null ? null : Number(row.mantenimiento),
        };
      });

    // Se cargan las tablas manuales de AMBOS paises: el modo combinado pre-2026
    // necesita los costos de los dos para armar los agregados de region.
    const cargarPais = async (p) => {
      const vacio = () => [];
      const [pub, pubAA, hw, hwAA, gest, gestAA, fijos, fijosAA] = await Promise.all([
        getCostosPublicidad({ pais: p, ano: selectedYear }).catch(vacio),
        getCostosPublicidad({ pais: p, ano: selectedYear - 1 }).catch(vacio),
        getCostosHardwareFlex({ pais: p, ano: selectedYear }).catch(vacio),
        getCostosHardwareFlex({ pais: p, ano: selectedYear - 1 }).catch(vacio),
        client.from("costos_gestion_contenidos").select("*").eq("pais", p).eq("ano", selectedYear),
        client
          .from("costos_gestion_contenidos")
          .select("*")
          .eq("pais", p)
          .eq("ano", selectedYear - 1),
        getCostosFijos({ pais: p, ano: selectedYear }).catch(vacio),
        getCostosFijos({ pais: p, ano: selectedYear - 1 }).catch(vacio),
      ]);
      return {
        publicidad: normPublicidad(pub),
        publicidadAA: normPublicidad(pubAA),
        hardwareFlex: hw || [],
        hardwareFlexAA: hwAA || [],
        gestion: gest.data || [],
        gestionAA: gestAA.data || [],
        rawFijos: fijos || [],
        // FASE 1b (2026-10-01): expone las filas crudas del año anterior, ya traídas arriba
        // (`fijosAA`) pero antes solo se exponía normalizadas — `index.js` necesita el
        // mismo patrón "existe fila real vs. proyectar" que usa el año en curso (serieManualFija).
        rawFijosAA: fijosAA || [],
        fijos: normFijos(fijos),
        fijosAA: normFijos(fijosAA),
      };
    };

    const fetchManual = async () => {
      try {
        const porPaisManual = {};
        await Promise.all(
          PAISES.map(async (p) => {
            porPaisManual[p] = await cargarPais(p);
          })
        );
        if (cancelado) return;
        setManual({ ...porPaisManual[pais], porPais: porPaisManual });
      } catch (err) {
        console.error("ERv2: error cargando tablas de costo manual", err);
      }
    };

    fetchManual();
    return () => {
      cancelado = true;
    };
  }, [pais, selectedYear]);

  // ─── Empleados de AMBOS países (el modo combinado necesita el RRHH de los dos) ───
  useEffect(() => {
    let cancelado = false;
    const fetchEmpleadosAmbos = async () => {
      try {
        const resultado = {};
        await Promise.all(
          PAISES.map(async (p) => {
            const base = await getEmpleados(p).catch(() => []);
            resultado[p] = await Promise.all(
              (base || []).map(async (emp) => ({
                ...emp,
                remuneraciones: (await getRemuneracionesEmpleado(emp.id).catch(() => [])) || [],
              }))
            );
          })
        );
        if (!cancelado) setEmpleadosPorPais(resultado);
      } catch (err) {
        console.error("ERv2: error cargando empleados de ambos países", err);
      }
    };
    fetchEmpleadosAmbos();
    return () => {
      cancelado = true;
    };
  }, []);

  // ─── Costo del SII SIN CLASIFICAR (cobertura de cuenta_er) ───
  // ANTES contaba `estado_vinculacion='pendiente'` de emitidas + recibidas. Eso mide el eje
  // de PAGO, no el de clasificación: sobre-contaba las pendientes YA clasificadas (que sí
  // devengan) y OMITÍA las 'vinculada' sin cuenta_er — el costo que de verdad no entra al ER
  // (128 facturas, ~$40M invisibles). Es la misma descontaminación de la capa de Compras
  // (regla 10.65): el warning ahora mide lo que le FALTA al ER, no el flag sobrecargado.
  //
  // Solo RECIBIDAS: las emitidas son ventas, no se clasifican con cuenta_er. Se excluyen las
  // NC (61, no son costo), la DIN (914 — Aduana, no es gasto a clasificar, ver documentosCosto.js)
  // y los activos (tipo_contable='activo', van a Depreciación), igual que el motor `costosER`.
  // `cuenta_er` vacío ('') cuenta como sin clasificar, como en costosER.
  //
  // PASO 6 (2026-10-07) — "facturas sin clasificar" tiene que salir de la MISMA fuente que usan
  // los costos (`costosER.js`), no de un criterio propio: antes, una factura con `cuenta_er`
  // vacío SE CONTABA acá aunque estuviera anulada por una NC `anula_error` (el motor de costos ya
  // la trata como si no existiera, 10.223) — divergía en silencio (10.177). Se agrega
  // `construirExclusionNcRecibidas` (misma función que usa `agregarCostosER`) + el corte duro
  // `INICIO_COSTOS_SII` (el motor de costos NUNCA clasifica nada antes de esa fecha — para un año
  // íntegramente anterior, el aviso correcto es 0, no "lo que falte clasificar").
  useEffect(() => {
    if (!pais || pais === "Region") return;
    let cancelado = false;

    const fetchDTEs = async () => {
      try {
        if (selectedYear < Number(INICIO_COSTOS_SII.slice(0, 4))) {
          if (!cancelado) setDtesSinVincular(0);
          return;
        }
        const desde = `${selectedYear}-01-01`;
        const hasta = `${selectedYear}-12-31`;
        // Universo COMPLETO (incluye NC) para poder construir la exclusión — la cuenta real se
        // filtra DESPUÉS, igual que `agregarCostosER`.
        const recRes = await client
          .from("facturas_sii_recibidas")
          .select("id, cuenta_er, tipo_contable, tipo_dte, tipo_relacion_nc, documento_referenciado_id")
          .eq("pais", pais)
          .neq("tipo_dte", TIPO_DTE_DIN)
          .gte("fecha_emision", desde)
          .lte("fecha_emision", hasta);
        const excluidos = construirExclusionNcRecibidas(recRes.data || []);
        const sinClasificar = (recRes.data || []).filter(
          (f) => !esNotaCredito(f) && !excluidos.has(Number(f.id)) && f.tipo_contable !== "activo" && !f.cuenta_er
        ).length;
        if (!cancelado) setDtesSinVincular(sinClasificar);
      } catch {
        /* silenciar si las tablas no existen aun */
      }
    };

    // PASO 6 (2026-10-07) — "ventas pendientes de vincular" tiene que usar la MISMA regla que
    // excluye esas ventas de la facturación (`construirRefKeysVinculadosSII`/`registrosIngresoSII`,
    // ingresosER.js): año de la factura == selectedYear Y excluida de NC `anula_error`/sin
    // clasificar (`construirExclusionNcEmitidas`). Antes este mapa no aplicaba NINGUNA de las dos
    // — una venta con la factura anulada por NC seguía leyéndose "vinculada" acá (no se cuenta
    // como pendiente) aunque `registrosIngresoSII` ya la excluye de la celda (no cuenta como
    // ingreso): la venta desaparecía de las dos vistas a la vez, sin aviso (10.177). Se retira
    // también el fallback por `facturanumero` (`map[folio]`) — sin equivalente en el camino de
    // Facturación, una venta podía "desaparecer" igual por ese atajo.
    const fetchVinculaciones = async () => {
      try {
        const { data } = await client
          .from("facturas_sii_emitidas")
          .select(
            "folio, id, fecha_emision, tipo_dte, tipo_relacion_nc, documento_referenciado_id, " +
              "facturas_sii_vinculaciones(venta_tipo, venta_ref_id, venta_ref_index, linea_id)"
          )
          .eq("pais", pais)
          .eq("estado_vinculacion", "vinculada");
        const { excluidas } = construirExclusionNcEmitidas(data || []);
        const map = { _byRef: {} };
        (data || []).forEach((f) => {
          if (esNotaCredito(f)) return; // las NC nunca vinculan su propia venta.
          if (excluidas.has(Number(f.id))) return;
          const fechaObj = f.fecha_emision ? parseLocalYMD(String(f.fecha_emision).slice(0, 10)) : null;
          if (!fechaObj || fechaObj.getFullYear() !== selectedYear) return;
          (f.facturas_sii_vinculaciones || []).forEach((v) => {
            const key = refKeyDeVinculacion(v);
            if (key) map._byRef[key] = { facturaId: f.id, folio: f.folio };
          });
        });
        if (!cancelado) setSiiVinculacionesMap(map);
      } catch {
        if (!cancelado) setSiiVinculacionesMap({ _byRef: {} });
      }
    };

    fetchDTEs();
    fetchVinculaciones();
    return () => {
      cancelado = true;
    };
  }, [pais, selectedYear]);

  const chile = porPais.Chile || {};
  const peru = porPais["Perú"] || {};
  const sesion = porPais[pais] || {};
  const otro = pais === "Chile" ? peru : chile;

  // Los hooks de BTL filtran por año, pero necesitan el set del año anterior aparte.
  const btlDelAno = (filas, ano) =>
    (filas || []).filter((r) => r.facturafecha && fechaLocalDeNegocio(r.facturafecha).getFullYear() === ano);

  return {
    // País de sesión (alias, como en el v1)
    oportunidades: sesion.oportunidades || [],
    contratos: sesion.contratos || [],
    clientes: sesion.clientes || {},
    intermediarios: sesion.intermediarios || {},
    edificios: sesion.edificios || {},
    snapMap: sesion.snapMap || null,
    pctMap: sesion.pctMap || null,
    costosMap: sesion.costosMap || null,
    negociosCarteleria: sesion.negociosCarteleria || [],
    salesBTL: btlDelAno(sesion.salesBTL, selectedYear),
    salesBTLAnoAnterior: btlDelAno(sesion.salesBTL, selectedYear - 1),
    salesBTLOtroPais: btlDelAno(otro.salesBTL, selectedYear),
    // Desdoblado por país (modo combinado)
    oportunidadesChile: chile.oportunidades || [],
    oportunidadesPeru: peru.oportunidades || [],
    contratosChile: chile.contratos || [],
    contratosPeru: peru.contratos || [],
    clientesChile: chile.clientes || {},
    clientesPeru: peru.clientes || {},
    intermediariosChile: chile.intermediarios || {},
    intermediariosPeru: peru.intermediarios || {},
    edificiosChile: chile.edificios || {},
    edificiosPeru: peru.edificios || {},
    snapMapChile: chile.snapMap || null,
    snapMapPeru: peru.snapMap || null,
    pctMapChile: chile.pctMap || null,
    pctMapPeru: peru.pctMap || null,
    costosMapChile: chile.costosMap || null,
    costosMapPeru: peru.costosMap || null,
    negociosCarteleriaChile: chile.negociosCarteleria || [],
    negociosCarteleriaPeru: peru.negociosCarteleria || [],
    salesBTLChile: btlDelAno(chile.salesBTL, selectedYear),
    salesBTLPeru: btlDelAno(peru.salesBTL, selectedYear),
    // Comunes
    users,
    proveedoresPorId,
    costosGestionData,
    depreciacionMensual: depAmort[pais]?.depreciacion || CERO12(),
    amortizacionMensual: depAmort[pais]?.amortizacion || CERO12(),
    depreciacionMensualChile: depAmort.Chile?.depreciacion || CERO12(),
    depreciacionMensualPeru: depAmort["Perú"]?.depreciacion || CERO12(),
    amortizacionMensualChile: depAmort.Chile?.amortizacion || CERO12(),
    amortizacionMensualPeru: depAmort["Perú"]?.amortizacion || CERO12(),
    // FASE E2c: gemelos un año atrás, los necesita la ventana LTM.
    depreciacionMensualAnoAnterior: depAmortAnoAnterior[pais]?.depreciacion || CERO12(),
    amortizacionMensualAnoAnterior: depAmortAnoAnterior[pais]?.amortizacion || CERO12(),
    // Vista de país único: la porción de la sesión (shape intacto para sus consumidores).
    facturasSII,
    facturasExternas,
    boletasHonorarios,
    // Vista de región: cada pata del combinado toma la suya, sin depender de la sesión.
    facturasSIIChile: facturasSIIPorPais.Chile || VACIO,
    facturasSIIPeru: facturasSIIPorPais["Perú"] || VACIO,
    facturasExternasChile: facturasExternasPorPais.Chile || VACIO,
    facturasExternasPeru: facturasExternasPorPais["Perú"] || VACIO,
    boletasHonorariosChile: boletasHonorariosPorPais.Chile || VACIO,
    boletasHonorariosPeru: boletasHonorariosPorPais["Perú"] || VACIO,
    inversionMensual: inversionPorPais[pais] || CERO12(),
    inversionMensualChile: inversionPorPais.Chile || CERO12(),
    inversionMensualPeru: inversionPorPais["Perú"] || CERO12(),
    valorActivosMensual: valorActivosPorPais[pais] || CERO12(),
    valorActivosMensualChile: valorActivosPorPais.Chile || CERO12(),
    valorActivosMensualPeru: valorActivosPorPais["Perú"] || CERO12(),
    empleadosRRHHChile: empleadosPorPais.Chile || [],
    empleadosRRHHPeru: empleadosPorPais["Perú"] || [],
    manual,
    dtesSinVincular,
    siiVinculacionesMap,
    facturasEmitidasChile,
    lineaIdATipo,
    // El modo combinado del v1 exige tener los datos de ambos países cargados.
    datosCombinadosCargados: Boolean(porPais.Chile && porPais["Perú"]),
    isLoading,
  };
};
